/**
 * 操控变形的仿射 MLS 位移场（薄封装，仿 `engine/liquify/warp.ts`）。
 * 数学在 `crates/core/src/puppet.rs`；这里只做数组拆分与 wasm 调用。
 */
import { mls_affine_field } from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';
import type { Point, Rect } from '../liquify/field';

export interface MlsPin {
  /** 拖动后的当前位置（角色互换：MLS 公式里的"参考点集" from）。 */
  readonly current: Point;
  /** 放置时的原始位置（角色互换：MLS 公式里的"目标点集" to）。 */
  readonly origin: Point;
}

export interface MlsFieldOptions {
  readonly signal?: AbortSignal;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/**
 * 对 `bounds` 覆盖的矩形逐像素求仿射 MLS 位移场（反向映射，见 puppet.rs
 * 文件头注释的角色互换说明）。`pins` 为空时不调用 wasm，直接返回全零场
 * （零图钉时液化式的"跳过"同款早退，不值得为此走一次 wasm 调用）。
 */
export async function computeMlsField(
  pins: readonly MlsPin[],
  bounds: Rect,
  options: MlsFieldOptions = {},
): Promise<{ dx: Float32Array; dy: Float32Array }> {
  const size = bounds.width * bounds.height;
  if (pins.length === 0) {
    return { dx: new Float32Array(size), dy: new Float32Array(size) };
  }

  throwIfAborted(options.signal);
  await ensureKernel();
  throwIfAborted(options.signal);

  const fromX = new Float32Array(pins.length);
  const fromY = new Float32Array(pins.length);
  const toX = new Float32Array(pins.length);
  const toY = new Float32Array(pins.length);
  pins.forEach((pin, i) => {
    fromX[i] = pin.current.x;
    fromY[i] = pin.current.y;
    toX[i] = pin.origin.x;
    toY[i] = pin.origin.y;
  });

  const interleaved = mls_affine_field(fromX, fromY, toX, toY, bounds.x, bounds.y, bounds.width, bounds.height);
  const dx = new Float32Array(size);
  const dy = new Float32Array(size);
  for (let i = 0; i < size; i++) {
    dx[i] = interleaved[i * 2]!;
    dy[i] = interleaved[i * 2 + 1]!;
  }
  return { dx, dy };
}
